import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Heart, MessageCircle, Bookmark, ShieldCheck, Send,
  Plus, Loader2, TrendingUp, Clock, DollarSign,
  ChevronDown, ChevronUp, ChevronRight, X, Store, Tag, Users, Sparkles,
  Camera, CameraOff, MapPin, Navigation, UserPlus, UserCheck, Award, Star,
  Bell, BellOff, Settings, Trash2, Flag, Trophy, Flame, Zap, Crown, Target, Share2, ExternalLink,
  LocateFixed, Shield, ArrowRight
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { registerPushSubscription, unregisterPushSubscription, isPushSupported } from "../lib/pushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getUserId = () => localStorage.getItem("community_user_id");
const setUserId = (id) => localStorage.setItem("community_user_id", id);
const getUsername = () => localStorage.getItem("community_username") || "Anonymous";
const setStoredUsername = (name) => localStorage.setItem("community_username", name);

async function ensureUser() {
  let uid = getUserId();
  const res = await fetch(`${BACKEND_URL}/api/community/user${uid ? `?user_id=${uid}` : ""}`);
  if (res.ok) {
    const user = await res.json();
    setUserId(user.id);
    setStoredUsername(user.username);
    return user;
  }
  return null;
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Location Consent ─────────────────────────────────────────────────────

const LOCATION_CONSENT_KEY = "foodz_location_consent";

const getLocationConsent = () => localStorage.getItem(LOCATION_CONSENT_KEY);
const setLocationConsent = (val) => localStorage.setItem(LOCATION_CONSENT_KEY, val);

/**
 * Request geolocation only if user has previously consented.
 * Returns null if no consent or geolocation unavailable.
 */
const requestLocationWithConsent = (onSuccess, onDenied) => {
  if (!navigator.geolocation) { onDenied?.(); return; }
  const consent = getLocationConsent();
  if (consent !== "granted") { onDenied?.(); return; }
  navigator.geolocation.getCurrentPosition(onSuccess, onDenied, { timeout: 8000, enableHighAccuracy: true });
};

const LocationConsentPrompt = ({ onConsent, onDismiss }) => (
  <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" data-testid="location-consent-overlay">
    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4 animate-in zoom-in-95" data-testid="location-consent-modal">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
          <LocateFixed className="h-5 w-5 text-violet-600" />
        </div>
        <h3 className="font-bold text-gray-900">Enable Location Services</h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">
        Foodz Lizts uses your location to:
      </p>
      <ul className="text-sm text-gray-600 space-y-1.5 ml-1">
        <li className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" /> Find stores near you when posting deals</li>
        <li className="flex items-start gap-2"><Navigation className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" /> Show deals in your area</li>
        <li className="flex items-start gap-2"><Store className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" /> Provide directions to deal locations</li>
      </ul>
      <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg">
        <Shield className="h-4 w-4 text-green-600 flex-shrink-0" />
        <p className="text-xs text-gray-500">Your location is never stored or shared with other users. It's only used in real-time to improve your experience.</p>
      </div>
      <div className="flex gap-3 pt-1">
        <Button
          variant="outline"
          className="flex-1 text-sm"
          onClick={() => { setLocationConsent("denied"); onDismiss(); }}
          data-testid="location-consent-deny"
        >
          Not Now
        </Button>
        <Button
          className="flex-1 text-sm bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-0"
          onClick={() => { setLocationConsent("granted"); onConsent(); }}
          data-testid="location-consent-allow"
        >
          <LocateFixed className="h-3.5 w-3.5 mr-1" /> Enable
        </Button>
      </div>
    </div>
  </div>
);

// ── Get Directions Helper ───────────────────────────────────────────────

const getDirectionsUrl = (deal) => {
  if (deal.latitude && deal.longitude) {
    return `https://www.google.com/maps/dir/?api=1&destination=${deal.latitude},${deal.longitude}`;
  }
  if (deal.store_address) {
    const encoded = encodeURIComponent(`${deal.store_name || ""} ${deal.store_address}`.trim());
    return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
  }
  return null;
};

const DirectionsLink = ({ deal }) => {
  const url = getDirectionsUrl(deal);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
      onClick={(e) => e.stopPropagation()}
      data-testid={`directions-link-${deal.id}`}
    >
      <Navigation className="h-3 w-3" />
      Get Directions
    </a>
  );
};

// ── Image Lightbox ──────────────────────────────────────────────────────

const ImageLightbox = ({ src, alt, onClose }) => (
  <div
    className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
    onClick={onClose}
    data-testid="image-lightbox"
  >
    <button
      onClick={onClose}
      className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-colors z-10"
      data-testid="lightbox-close"
    >
      <X className="h-6 w-6" />
    </button>
    <img
      src={src}
      alt={alt || "Deal photo"}
      className="max-w-full max-h-[85vh] object-contain rounded-lg"
      onClick={(e) => e.stopPropagation()}
    />
  </div>
);

// ── Deal of the Day ─────────────────────────────────────────────────────

const DealOfTheDay = ({ deal, liked, saved, onLike, onSave }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!deal) return null;

  return (
    <Card className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-amber-300 shadow-sm overflow-hidden relative" data-testid="deal-of-the-day">
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-amber-200/50 to-transparent rounded-bl-full" />
      <CardContent className="p-0">
        <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1">
          <Award className="h-4 w-4 text-amber-600" />
          <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Deal of the Day</span>
          <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
        </div>
        <div className="flex gap-3 px-3 pb-2">
          {deal.image_url && (
            <img
              src={deal.image_url}
              alt={deal.title}
              className="w-20 h-20 object-contain bg-white rounded-lg flex-shrink-0 border border-amber-100 cursor-zoom-in hover:ring-2 hover:ring-amber-300 transition-all"
              onError={(e) => { e.target.style.display = "none"; }}
              onClick={() => setLightbox({ src: deal.image_url, alt: deal.title })}
              data-testid="dotd-image-zoom"
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm text-gray-900 leading-snug">{deal.title}</h3>
            {deal.store_name && (
              <p className="text-xs text-amber-700 flex items-center gap-0.5 mt-0.5">
                <Store className="h-3 w-3" />{deal.store_name}
              </p>
            )}
            {deal.store_address && (
              <p className="text-xs text-gray-400 flex items-center gap-0.5 truncate">
                <MapPin className="h-2.5 w-2.5 flex-shrink-0" />{deal.store_address}
              </p>
            )}
            {(deal.store_address || (deal.latitude && deal.longitude)) && (
              <DirectionsLink deal={deal} />
            )}
            <div className="flex items-baseline gap-2 mt-1">
              {deal.price != null && (
                <span className="text-xl font-bold text-green-600">${deal.price.toFixed(2)}</span>
              )}
              {deal.original_price != null && deal.original_price > (deal.price || 0) && (
                <span className="text-sm text-gray-400 line-through">${deal.original_price.toFixed(2)}</span>
              )}
              {deal.savings > 0 && (
                <Badge className="bg-green-100 text-green-700 text-xs">Save ${deal.savings.toFixed(2)}</Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Posted by <span className="font-medium">{deal.posted_by}</span> {timeAgo(deal.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center border-t border-amber-200/50 px-1 bg-amber-50/30">
          <button
            onClick={() => onLike(deal.id, deal.type)}
            className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs ${liked ? "text-red-500 font-semibold" : "text-gray-500"}`}
            data-testid="dotd-like"
          >
            <Heart className={`h-3.5 w-3.5 ${liked ? "fill-red-500" : ""}`} />
            {(deal.likes_count || 0) > 0 && deal.likes_count}
          </button>
          <button
            onClick={() => onSave(deal.id, deal.type, deal)}
            className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs ${saved ? "text-amber-500 font-semibold" : "text-gray-500"}`}
            data-testid="dotd-save"
          >
            <Bookmark className={`h-3.5 w-3.5 ${saved ? "fill-amber-500" : ""}`} />
            Save
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Location Filter ─────────────────────────────────────────────────────

const LocationFilter = ({ currentLocation, onLocationChange }) => {
  const [customZip, setCustomZip] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [showConsentPrompt, setShowConsentPrompt] = useState(false);

  const doDetectGPS = () => {
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`
          );
          if (res.ok) {
            const data = await res.json();
            const zip = data?.address?.postcode;
            if (zip) {
              onLocationChange(zip);
            }
          }
        } catch (e) {
          console.error("Geocode failed:", e);
        }
        setDetecting(false);
      },
      () => setDetecting(false),
      { timeout: 10000 }
    );
  };

  const detectGPS = () => {
    if (!navigator.geolocation) return;
    const consent = getLocationConsent();
    if (!consent) {
      setShowConsentPrompt(true);
      return;
    }
    if (consent === "denied") {
      toast("Location access was previously denied. You can enable it in your browser settings.");
      return;
    }
    doDetectGPS();
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="location-filter">
      {["local", "all"].map((loc) => (
        <button
          key={loc}
          onClick={() => { onLocationChange(loc === "local" ? null : "all"); setShowCustom(false); }}
          className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
            (currentLocation === null && loc === "local") || currentLocation === "all" && loc === "all"
              ? "bg-violet-500 text-white font-semibold"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
          data-testid={`loc-${loc}`}
        >
          {loc === "local" ? <><MapPin className="h-3 w-3 inline mr-0.5" />Local</> : "All Areas"}
        </button>
      ))}
      <button
        onClick={detectGPS}
        disabled={detecting}
        className="px-2.5 py-1 rounded-full text-xs bg-gray-100 text-gray-500 hover:bg-blue-100 hover:text-blue-600 transition-colors"
        data-testid="loc-gps"
      >
        {detecting ? <Loader2 className="h-3 w-3 inline animate-spin" /> : <Navigation className="h-3 w-3 inline mr-0.5" />}
        GPS
      </button>
      <button
        onClick={() => setShowCustom(!showCustom)}
        className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
          showCustom || (currentLocation && currentLocation !== "all") ? "bg-violet-500 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
        }`}
        data-testid="loc-custom"
      >
        {currentLocation && currentLocation !== "all" && currentLocation !== null ? currentLocation : "Zip Code"}
      </button>
      {showCustom && (
        <div className="flex gap-1 w-full mt-1">
          <Input
            value={customZip}
            onChange={(e) => setCustomZip(e.target.value)}
            placeholder="Enter zip..."
            className="h-7 text-xs flex-1"
            maxLength={10}
            data-testid="custom-zip-input"
          />
          <Button
            size="sm"
            onClick={() => { if (customZip.trim()) { onLocationChange(customZip.trim()); setShowCustom(false); } }}
            className="h-7 text-xs px-2 bg-violet-500"
            data-testid="custom-zip-go"
          >
            Go
          </Button>
        </div>
      )}
      {showConsentPrompt && (
        <LocationConsentPrompt
          onConsent={() => { setShowConsentPrompt(false); doDetectGPS(); }}
          onDismiss={() => setShowConsentPrompt(false)}
        />
      )}
    </div>
  );
};

// ── Deal Card ───────────────────────────────────────────────────────────

const DealCard = ({
  deal, liked, saved, verified, isFollowing, userId, reported, referralCode,
  onLike, onSave, onVerify, onFollow, onToggleComments, onDelete, onReport
}) => {
  const isCommunity = deal.type === "community";
  const isOwnPost = deal.user_id === userId;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  return (
    <Card className="bg-white" data-testid={`deal-card-${deal.id}`}>
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-3 pt-3 pb-1">
          {isCommunity && deal.avatar_url ? (
            <img src={deal.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover border border-gray-200" />
          ) : (
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${
              isCommunity ? "bg-gradient-to-br from-violet-500 to-fuchsia-500" : "bg-orange-400"
            }`}>
              {isCommunity ? (deal.posted_by || "A")[0].toUpperCase() : "W"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-xs font-semibold text-gray-800 truncate">
                {isCommunity ? deal.posted_by || "Anonymous" : deal.store || deal.store_name || "Price Comparison"}
              </p>
              {isCommunity && !isOwnPost && (
                <button
                  onClick={() => onFollow(deal.user_id)}
                  className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs transition-colors ${
                    isFollowing
                      ? "bg-violet-100 text-violet-600"
                      : "bg-gray-100 text-gray-400 hover:bg-violet-50 hover:text-violet-500"
                  }`}
                  data-testid={`follow-${deal.user_id}`}
                >
                  {isFollowing ? <UserCheck className="h-3 w-3" /> : <UserPlus className="h-3 w-3" />}
                  {isFollowing ? "Following" : "Follow"}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400">{timeAgo(deal.created_at)}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isCommunity && deal.verified && (
              <Badge className="bg-green-100 text-green-700 text-xs">
                <ShieldCheck className="h-3 w-3 mr-0.5" />Verified
              </Badge>
            )}
            <Badge className={`text-xs ${isCommunity ? "bg-violet-100 text-violet-700" : "bg-orange-100 text-orange-700"}`}>
              {isCommunity ? "Community" : "Store Ad"}
            </Badge>
            {isCommunity && isOwnPost && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }}
                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                title="Delete your post"
                data-testid={`delete-own-${deal.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            {isCommunity && !isOwnPost && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowReportMenu(true); }}
                className={`p-1 rounded-full transition-colors ${
                  reported ? "text-red-500 bg-red-50" : "text-gray-400 hover:text-red-500 hover:bg-red-50"
                }`}
                title={reported ? "Already reported" : "Report this deal"}
                disabled={reported}
                data-testid={`report-deal-${deal.id}`}
              >
                <Flag className={`h-3.5 w-3.5 ${reported ? "fill-current" : ""}`} />
              </button>
            )}
          </div>
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="mx-3 mb-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 font-medium mb-2">Delete this deal?</p>
            <p className="text-xs text-red-600 mb-3">This action cannot be undone.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button size="sm" className="flex-1 text-xs h-7 bg-red-600 hover:bg-red-700 text-white" onClick={() => { onDelete(deal.id); setShowDeleteConfirm(false); }} data-testid={`confirm-delete-${deal.id}`}>
                <Trash2 className="h-3 w-3 mr-1" />Delete
              </Button>
            </div>
          </div>
        )}

        {/* Report Menu */}
        {showReportMenu && (
          <div className="mx-3 mb-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 font-medium mb-2">Why are you reporting this?</p>
            <div className="space-y-1.5">
              {["Misleading price", "Expired deal", "Spam or scam", "Inappropriate content", "Other"].map(reason => (
                <button
                  key={reason}
                  className="w-full text-left text-xs px-3 py-1.5 rounded-md hover:bg-amber-100 text-gray-700 transition-colors"
                  onClick={() => { onReport(deal.id, reason); setShowReportMenu(false); }}
                  data-testid={`report-reason-${reason.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {reason}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" className="w-full mt-2 text-xs h-7" onClick={() => setShowReportMenu(false)}>Cancel</Button>
          </div>
        )}

        {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}

        {deal.image_url && (
          <div className="px-3 py-2">
            <img
              src={deal.image_url}
              alt={deal.title || deal.product_name}
              className="w-full h-44 object-contain bg-gray-50 rounded-lg cursor-zoom-in hover:opacity-90 transition-opacity"
              onError={(e) => { e.target.style.display = "none"; }}
              onClick={() => setLightbox({ src: deal.image_url, alt: deal.title || deal.product_name })}
              data-testid={`deal-image-zoom-${deal.id}`}
            />
          </div>
        )}

        <div className="px-3 pb-2">
          <h3 className="font-semibold text-sm text-gray-900 leading-snug">
            {deal.title || deal.product_name || deal.description}
          </h3>
          {deal.description && deal.description !== deal.title && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{deal.description}</p>
          )}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {deal.store_name && isCommunity && (
              <span className="text-xs text-gray-500 flex items-center gap-0.5">
                <Store className="h-3 w-3" />{deal.store_name}
              </span>
            )}
            {deal.category && (
              <span className="text-xs text-gray-400 flex items-center gap-0.5">
                <Tag className="h-3 w-3" />{deal.category}
              </span>
            )}
          </div>
          {deal.store_address && isCommunity && (
            <p className="text-xs text-gray-400 flex items-center gap-0.5 mt-0.5 truncate">
              <MapPin className="h-2.5 w-2.5 flex-shrink-0" />{deal.store_address}
            </p>
          )}
          {isCommunity && (deal.store_address || (deal.latitude && deal.longitude)) && (
            <div className="mt-0.5"><DirectionsLink deal={deal} /></div>
          )}
          {deal.source_url && (
            <a
              href={deal.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1.5 text-xs text-violet-600 hover:text-violet-800 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
              data-testid={`source-link-${deal.id}`}
            >
              <ExternalLink className="h-3 w-3" />
              View Original Source
            </a>
          )}
          <div className="flex items-baseline gap-2 mt-1.5">
            {deal.price != null && (
              <span className="text-lg font-bold text-green-600">${deal.price.toFixed(2)}</span>
            )}
            {deal.original_price != null && deal.original_price > (deal.price || 0) && (
              <span className="text-sm text-gray-400 line-through">${deal.original_price.toFixed(2)}</span>
            )}
            {deal.savings > 0 && (
              <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Save ${deal.savings.toFixed(2)}</Badge>
            )}
          </div>
        </div>

        <div className="flex items-center border-t border-gray-100 px-1">
          <button
            onClick={() => onLike(deal.id, deal.type)}
            className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs transition-colors ${liked ? "text-red-500 font-semibold" : "text-gray-500 hover:text-red-400"}`}
            data-testid={`like-${deal.id}`}
          >
            <Heart className={`h-4 w-4 ${liked ? "fill-red-500" : ""}`} />
            {(deal.likes_count || 0) > 0 && <span>{deal.likes_count}</span>}
          </button>
          <button
            onClick={() => onToggleComments(deal.id)}
            className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs text-gray-500 hover:text-blue-500"
            data-testid={`comment-btn-${deal.id}`}
          >
            <MessageCircle className="h-4 w-4" />
            {(deal.comments_count || 0) > 0 && <span>{deal.comments_count}</span>}
          </button>
          <button
            onClick={() => onSave(deal.id, deal.type, deal)}
            className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs transition-colors ${saved ? "text-amber-500 font-semibold" : "text-gray-500 hover:text-amber-400"}`}
            data-testid={`save-${deal.id}`}
          >
            <Bookmark className={`h-4 w-4 ${saved ? "fill-amber-500" : ""}`} />
            {(deal.saves_count || 0) > 0 && <span>{deal.saves_count}</span>}
          </button>
          {isCommunity && !isOwnPost && (
            <button
              onClick={() => onVerify(deal.id)}
              className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs transition-colors ${verified ? "text-green-600 font-semibold" : "text-gray-500 hover:text-green-500"}`}
              data-testid={`verify-${deal.id}`}
            >
              <ShieldCheck className={`h-4 w-4 ${verified ? "fill-green-100" : ""}`} />
              {(deal.verify_count || 0) > 0 && <span>{deal.verify_count}</span>}
            </button>
          )}
          <button
            onClick={() => shareDeal(deal, referralCode, userId)}
            className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs text-gray-500 hover:text-green-600 transition-colors"
            data-testid={`share-${deal.id}`}
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Share Deal Function ──────────────────────────────────────────────────

const generateShareCard = async (deal) => {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, 1080, 1350);

  // Green header bar
  const grd = ctx.createLinearGradient(0, 0, 1080, 0);
  grd.addColorStop(0, "#16a34a");
  grd.addColorStop(1, "#15803d");
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, 1080, 140);

  // App name
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 48px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("Foodz Lizts", 540, 70);
  ctx.font = "24px Arial, sans-serif";
  ctx.fillStyle = "#bbf7d0";
  ctx.fillText("Community Deal", 540, 110);

  // Deal image
  let imageY = 180;
  if (deal.image_url) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = deal.image_url;
      });
      const imgH = 450;
      ctx.save();
      ctx.fillStyle = "#f9fafb";
      ctx.fillRect(60, imageY, 960, imgH);
      const scale = Math.min(960 / img.width, imgH / img.height);
      const iw = img.width * scale;
      const ih = img.height * scale;
      ctx.drawImage(img, 60 + (960 - iw) / 2, imageY + (imgH - ih) / 2, iw, ih);
      ctx.restore();
      imageY += imgH + 30;
    } catch {
      imageY += 10;
    }
  }

  // Title
  ctx.fillStyle = "#111827";
  ctx.font = "bold 42px Arial, sans-serif";
  ctx.textAlign = "left";
  const title = deal.title || deal.product_name || deal.description || "";
  const words = title.split(" ");
  let line = "";
  let ty = imageY + 10;
  for (const word of words) {
    const test = line + word + " ";
    if (ctx.measureText(test).width > 920 && line) {
      ctx.fillText(line.trim(), 80, ty);
      line = word + " ";
      ty += 52;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line.trim(), 80, ty);
  ty += 60;

  // Store & category
  if (deal.store_name) {
    ctx.fillStyle = "#6b7280";
    ctx.font = "28px Arial, sans-serif";
    ctx.fillText(`Store: ${deal.store_name}`, 80, ty);
    ty += 40;
  }

  // Price
  if (deal.price != null) {
    ctx.fillStyle = "#16a34a";
    ctx.font = "bold 72px Arial, sans-serif";
    ctx.fillText(`$${deal.price.toFixed(2)}`, 80, ty + 20);
    if (deal.original_price && deal.original_price > deal.price) {
      const priceW = ctx.measureText(`$${deal.price.toFixed(2)}`).width;
      ctx.fillStyle = "#9ca3af";
      ctx.font = "36px Arial, sans-serif";
      const origText = `$${deal.original_price.toFixed(2)}`;
      ctx.fillText(origText, 100 + priceW, ty + 10);
      const origW = ctx.measureText(origText).width;
      ctx.strokeStyle = "#9ca3af";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(100 + priceW, ty + 0);
      ctx.lineTo(100 + priceW + origW, ty + 0);
      ctx.stroke();
    }
    if (deal.savings > 0) {
      ctx.fillStyle = "#16a34a";
      ctx.font = "bold 32px Arial, sans-serif";
      ctx.fillText(`Save $${deal.savings.toFixed(2)}`, 80, ty + 70);
    }
  }

  // Bottom CTA bar
  const ctaGrd = ctx.createLinearGradient(0, 1200, 1080, 1200);
  ctaGrd.addColorStop(0, "#16a34a");
  ctaGrd.addColorStop(1, "#15803d");
  ctx.fillStyle = ctaGrd;
  ctx.fillRect(0, 1200, 1080, 150);

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 36px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("Get Foodz Lizts — Free for 14 Days", 540, 1260);
  ctx.font = "22px Arial, sans-serif";
  ctx.fillStyle = "#bbf7d0";
  ctx.fillText("foodzlizts.com", 540, 1300);

  return canvas.toDataURL("image/png");
};

const copyToClipboardFallback = (text) => {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
};

const shareDeal = async (deal, referralCode, userId) => {
  const refParam = referralCode ? `?ref=${referralCode}` : "";
  const shareUrl = `${window.location.origin}/share/${deal.id}${refParam}`;
  const shareText = deal.price != null
    ? `Check out this deal: ${deal.title || deal.product_name} — $${deal.price.toFixed(2)}${deal.savings > 0 ? ` (Save $${deal.savings.toFixed(2)})` : ""} on Foodz Lizts!`
    : `Check out this deal: ${deal.title || deal.product_name} on Foodz Lizts!`;

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Native share sheet — mobile only (desktop share UX is poor and expires gestures)
  if (isMobile && navigator.share) {
    try {
      let shared = false;
      try {
        const dataUrl = await generateShareCard(deal);
        if (dataUrl) {
          const res = await fetch(dataUrl);
          const blob = await res.blob();
          const file = new File([blob], "foodz-lizts-deal.png", { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ title: "Foodz Lizts Deal", text: shareText, url: shareUrl, files: [file] });
            shared = true;
          }
        }
      } catch (imgErr) {
        if (imgErr.name === "AbortError") return;
      }

      if (!shared) {
        await navigator.share({ title: "Foodz Lizts Deal", text: shareText, url: shareUrl });
      }
      toast.success("Deal shared!");
      return;
    } catch (e) {
      if (e.name === "AbortError") return;
      // Fall through to clipboard
    }
  }

  // Desktop & fallback: copy to clipboard
  const fullText = `${shareText}\n${shareUrl}`;
  try {
    await navigator.clipboard.writeText(fullText);
    toast.success("Deal link copied to clipboard!");
  } catch {
    try {
      copyToClipboardFallback(fullText);
      toast.success("Deal link copied to clipboard!");
    } catch {
      toast.error("Couldn't copy. Try copying this link: " + shareUrl);
    }
  }

  // Log share for notifications
  if (userId && deal.id) {
    fetch(`${BACKEND_URL}/api/community/share-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, deal_id: deal.id }),
    }).catch(() => {});
  }
};

// ── Comment Section ─────────────────────────────────────────────────────

const CommentSection = ({ dealId, onPost }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/community/comments/${dealId}`);
        if (res.ok) setComments((await res.json()).comments || []);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [dealId]);

  const handlePost = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: getUserId(), deal_id: dealId, deal_type: "community", text: text.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setComments((prev) => [data.comment, ...prev]);
        setText("");
        onPost?.();
      }
    } catch (e) { /* ignore */ }
    setPosting(false);
  };

  return (
    <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2 bg-gray-50/50" data-testid={`comments-${dealId}`}>
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment..."
          className="flex-1 h-8 text-xs"
          onKeyDown={(e) => e.key === "Enter" && handlePost()}
          data-testid={`comment-input-${dealId}`}
        />
        <Button size="sm" onClick={handlePost} disabled={posting || !text.trim()} className="h-8 px-2 bg-violet-500 hover:bg-violet-600" data-testid={`comment-send-${dealId}`}>
          {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-1">No comments yet. Be the first!</p>
      ) : (
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-violet-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-violet-700">{(c.username || "A")[0]}</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-700">{c.username || "Anonymous"}</span>
                <span className="text-xs text-gray-400 ml-1">{timeAgo(c.created_at)}</span>
                <p className="text-xs text-gray-600">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Store Autocomplete ──────────────────────────────────────────────────

const StoreAutocomplete = ({ value, onChange, onAddressChange, userId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [nearbyStores, setNearbyStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-detect GPS on mount and fetch nearby stores (only if user consented)
  useEffect(() => {
    if (!navigator.geolocation) return;
    const consent = getLocationConsent();
    if (consent !== "granted") { return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setGpsEnabled(true);
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserCoords(coords);
        try {
          const res = await fetch(`${BACKEND_URL}/api/community/nearby-stores?lat=${coords.lat}&lng=${coords.lng}&radius=2000&limit=6`);
          if (res.ok) {
            const data = await res.json();
            setNearbyStores(data.stores || []);
          }
        } catch { /* ignore */ }
        setGpsLoading(false);
      },
      () => { setGpsLoading(false); },
      { timeout: 8000, enableHighAccuracy: true }
    );
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); setOpen(nearbyStores.length > 0); return; }
    try {
      const params = new URLSearchParams({ q, limit: "6" });
      if (userCoords) {
        params.append("lat", userCoords.lat);
        params.append("lng", userCoords.lng);
      }
      const res = await fetch(`${BACKEND_URL}/api/community/store-search?${params}`);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.results || []);
        setOpen(true);
        setHighlighted(-1);
      }
    } catch { /* ignore */ }
  }, [userCoords, nearbyStores.length]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    onAddressChange?.("");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 180);
  };

  const selectStore = (item) => {
    onChange(item.name || item.display);
    onAddressChange?.(item.address || "");
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    const items = displayList();
    if (items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      selectStore(items[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Show nearby stores when input is empty, typed suggestions when typing
  const displayList = () => {
    if (suggestions.length > 0) {
      return suggestions.map(s => ({ ...s, type: s.source || "search" }));
    }
    if (nearbyStores.length > 0 && (!value || value.length < 2)) {
      return nearbyStores.map(s => ({ ...s, type: "nearby" }));
    }
    return [];
  };

  const items = displayList();
  const showDropdown = open && items.length > 0;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (items.length > 0) setOpen(true); else if (nearbyStores.length > 0) setOpen(true); }}
          placeholder={gpsLoading ? "Finding nearby stores..." : gpsEnabled ? "Nearby stores detected — tap to select" : "Start typing... Kroger, Walmart, ALDI"}
          className="text-sm pr-8"
          autoComplete="off"
          data-testid="post-deal-store"
        />
        {gpsEnabled && !gpsLoading && (
          <MapPin className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-green-500" />
        )}
        {gpsLoading && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 animate-spin" />
        )}
      </div>
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="store-suggestions-dropdown">
          {items[0]?.type === "nearby" && (
            <div className="px-3 py-1.5 text-xs text-green-600 font-medium bg-green-50/50 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Stores near you
            </div>
          )}
          {items.map((item, i) => (
            <button
              key={item.display + i}
              type="button"
              onClick={() => selectStore(item)}
              className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                i === highlighted ? "bg-violet-50 text-violet-700" : "hover:bg-gray-50 text-gray-700"
              }`}
              data-testid={`store-suggestion-${i}`}
            >
              {item.type === "nearby" ? (
                <MapPin className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
              ) : item.source === "photon" || item.source === "locationiq" ? (
                <MapPin className="h-3.5 w-3.5 text-violet-500 flex-shrink-0" />
              ) : (
                <Store className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <span className="block font-medium truncate">{item.name}</span>
                {item.address && <span className="block text-xs text-gray-400 truncate">{item.address}</span>}
              </div>
              {(item.source === "photon" || item.source === "locationiq") && (
                <span className="text-[10px] text-gray-300 flex-shrink-0 uppercase">{item.source === "locationiq" ? "IQ" : "OSM"}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Client-side image compression ──────────────────────────────────────
// Target: keep labels + prices sharp (start at 2560px longest edge, JPEG q0.92)
// then step down only as needed to fit under `targetMaxBytes` before the 10MB
// server limit. Returns the smallest acceptable blob.
async function compressForUpload(sourceBlob, targetMaxBytes = 9.5 * 1024 * 1024) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Could not read the captured image"));
    i.src = URL.createObjectURL(sourceBlob);
  });

  const attempts = [
    { maxDim: 2560, quality: 0.92 },
    { maxDim: 2560, quality: 0.85 },
    { maxDim: 2048, quality: 0.85 },
    { maxDim: 2048, quality: 0.78 },
    { maxDim: 1600, quality: 0.78 },
    { maxDim: 1280, quality: 0.72 },
    { maxDim: 1024, quality: 0.65 },
  ];

  let best = null;
  for (const step of attempts) {
    const scale = Math.min(1, step.maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", step.quality));
    if (!blob) continue;
    best = { blob, width: w, height: h, ...step };
    if (blob.size <= targetMaxBytes) break;
  }
  URL.revokeObjectURL(img.src);
  if (!best) throw new Error("Image encoding failed on this device");
  return best;
}

// ── Live Camera Capture (rear camera by default) ───────────────────────
const LiveCameraCapture = ({ onCapture, onCancel }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setErr("Live camera isn't supported on this browser. Choose a photo from your gallery instead.");
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 2560 },
            height: { ideal: 1440 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch (_e) { /* autoplay policy — user gesture already gave permission */ }
          setReady(true);
        }
      } catch (e) {
        let msg = `Camera error: ${e.message || e.name || "unknown"}`;
        if (e.name === "NotAllowedError" || e.name === "PermissionDeniedError") {
          msg = "Camera permission was denied. Enable camera access to snap a live photo, or pick one from your gallery.";
        } else if (e.name === "NotFoundError" || e.name === "OverconstrainedError") {
          msg = "No rear camera found on this device.";
        } else if (e.name === "NotReadableError") {
          msg = "Camera is already in use by another app.";
        }
        setErr(msg);
      }
    })();
    return () => {
      cancelled = true;
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Couldn't capture that frame — please try again.");
        return;
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const file = new File([blob], `deal-${Date.now()}.jpg`, { type: "image/jpeg" });
      onCapture(file);
    }, "image/jpeg", 0.95);
  };

  return (
    <div className="fixed inset-0 bg-black z-[60] flex flex-col" data-testid="live-camera-overlay">
      <div className="flex items-center justify-between p-3 text-white">
        <button
          type="button"
          onClick={onCancel}
          className="p-2 hover:bg-white/10 rounded-full"
          data-testid="live-camera-cancel"
          aria-label="Close camera"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="text-sm font-semibold">Snap Deal Photo</div>
        <div className="w-9" />
      </div>
      <div className="flex-1 flex items-center justify-center relative bg-black overflow-hidden">
        {err ? (
          <div className="p-6 text-center text-white max-w-sm" data-testid="live-camera-error">
            <CameraOff className="h-10 w-10 mx-auto mb-3 text-red-300" />
            <p className="text-sm leading-relaxed">{err}</p>
            <button
              type="button"
              onClick={onCancel}
              className="mt-4 text-xs underline text-white/80"
              data-testid="live-camera-fallback"
            >
              Pick from gallery instead
            </button>
          </div>
        ) : (
          <video
            ref={videoRef}
            playsInline
            muted
            className="max-h-full max-w-full object-contain"
            data-testid="live-camera-video"
          />
        )}
        {!ready && !err && (
          <Loader2 className="absolute h-8 w-8 text-white animate-spin" />
        )}
      </div>
      {!err && (
        <div className="p-6 flex items-center justify-center gap-4 bg-black">
          <button
            type="button"
            disabled={!ready}
            onClick={snap}
            className="w-16 h-16 rounded-full bg-white ring-4 ring-white/30 disabled:opacity-40 hover:scale-105 active:scale-95 transition-transform"
            data-testid="live-camera-snap"
            aria-label="Snap photo"
          />
        </div>
      )}
      <p className="text-center text-[10px] text-white/60 pb-3 px-4">
        Hold steady · Keep the price + label in sharp focus
      </p>
    </div>
  );
};

// ── Post Deal Form (with photo upload) ──────────────────────────────────

const PostDealForm = ({ onClose, onPosted, userGps }) => {
  const [form, setForm] = useState({
    title: "", description: "", store_name: "", store_address: "", price: "", original_price: "", category: ""
  });
  const [posting, setPosting] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(""); // "compressing" | "uploading" | "done"
  const fileRef = useRef(null);

  const categories = ["Produce", "Dairy", "Meat", "Bakery", "Frozen", "Beverages", "Snacks", "Pantry", "Household", "Other"];

  const uploadFile = async (file) => {
    setImagePreview(URL.createObjectURL(file));
    setUploading(true);
    setUploadStatus("compressing");
    try {
      let toSend = file;
      // Compress iff > 1.5MB — smaller shots don't need it and preserve max fidelity
      if (file.size > 1.5 * 1024 * 1024) {
        try {
          const result = await compressForUpload(file);
          toSend = new File([result.blob], `deal-${Date.now()}.jpg`, { type: "image/jpeg" });
          const savedKB = Math.round((file.size - result.blob.size) / 1024);
          if (savedKB > 20) {
            toast.success(`Compressed ${Math.round(file.size / 1024)}KB → ${Math.round(result.blob.size / 1024)}KB @ ${result.width}×${result.height}`, { duration: 2000 });
          }
        } catch (ce) {
          console.warn("Compression failed, uploading original:", ce);
          if (file.size > 9.5 * 1024 * 1024) {
            toast.error("Photo is too large and we couldn't shrink it. Please try again with a smaller shot.");
            setUploading(false);
            setUploadStatus("");
            setImagePreview(null);
            return;
          }
        }
      }
      setUploadStatus("uploading");
      const formData = new FormData();
      formData.append("file", toSend);
      const res = await fetch(`${BACKEND_URL}/api/community/upload`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        let msg;
        if (res.status === 413) {
          msg = "Photo is over the 10MB limit. Retake in a bit more light so it compresses smaller.";
        } else if (res.status === 415) {
          msg = "That file type isn't supported. Please share a JPEG or PNG.";
        } else if (res.status === 401 || res.status === 403) {
          msg = "You're not signed in. Please log in and try again.";
        } else {
          const text = await res.text().catch(() => "");
          msg = `Upload failed (${res.status}). ${text.slice(0, 120)}`;
        }
        toast.error(msg);
        setImagePreview(null);
        setImageUrl("");
      } else {
        const data = await res.json();
        setImageUrl(data.url || "");
        setUploadStatus("done");
        toast.success("Photo uploaded — labels look sharp!");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error(
        err?.message?.toLowerCase().includes("network")
          ? "Network hiccup while uploading. Check your connection and try again."
          : "Couldn't upload that photo. Please try again."
      );
      setImagePreview(null);
      setImageUrl("");
    } finally {
      setUploading(false);
      // Keep uploadStatus if success so UI shows "done"
    }
  };

  const handleGalleryChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    // Reset so selecting the same file twice still fires onChange
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleLiveCapture = (file) => {
    setCameraOpen(false);
    uploadFile(file);
  };

  const toTitleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return;
    setPosting(true);
    const storeName = form.store_name.trim() ? toTitleCase(form.store_name.trim()) : "";
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/deals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: getUserId(),
          title: form.title || form.description.slice(0, 60),
          description: form.description,
          store_name: storeName,
          store_address: form.store_address,
          price: form.price ? parseFloat(form.price) : null,
          original_price: form.original_price ? parseFloat(form.original_price) : null,
          category: form.category,
          image_url: imageUrl,
          latitude: userGps?.lat || null,
          longitude: userGps?.lng || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        // Save custom store name for future auto-suggestions
        if (storeName) {
          fetch(`${BACKEND_URL}/api/community/store-suggestions/custom`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: getUserId(), store_name: storeName }),
          }).catch(() => {});
        }
        onPosted?.(data.rewards || []);
        onClose();
      }
    } catch (e) { console.error("Post failed:", e); }
    setPosting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" data-testid="post-deal-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Share a Deal</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full" data-testid="close-post-deal">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {/* Photo upload - prominent */}
          <div
            className={`relative border-2 border-dashed rounded-xl transition-colors text-center ${
              imagePreview ? "border-violet-300 bg-violet-50" : "border-gray-200 hover:border-violet-300 hover:bg-violet-50/30"
            }`}
            data-testid="photo-upload-area"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleGalleryChange}
              className="hidden"
              data-testid="photo-input"
            />
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="Preview" className="w-full h-40 object-contain rounded-lg" />
                {uploading && (
                  <div className="absolute inset-0 bg-white/85 flex flex-col items-center justify-center rounded-lg gap-1">
                    <Loader2 className="h-6 w-6 text-violet-500 animate-spin" />
                    <span className="text-[11px] text-violet-700 font-medium" data-testid="photo-upload-status">
                      {uploadStatus === "compressing" ? "Compressing…" : "Uploading…"}
                    </span>
                  </div>
                )}
                {!uploading && imageUrl && (
                  <div className="absolute top-1 left-1 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="photo-uploaded-badge">
                    ✓ Uploaded
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setImagePreview(null);
                    setImageUrl("");
                    setUploadStatus("");
                  }}
                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5"
                  data-testid="photo-remove-btn"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="py-6 px-4">
                <Camera className="h-8 w-8 text-violet-400 mx-auto mb-2" />
                <p className="text-sm text-violet-700 font-semibold">Add a photo of the price tag</p>
                <p className="text-xs text-gray-500 mb-3">Sharp labels help other shoppers verify the deal</p>
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => setCameraOpen(true)}
                    className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors"
                    data-testid="open-live-camera-btn"
                  >
                    <Camera className="h-3.5 w-3.5" />
                    Snap Live
                  </button>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="text-xs font-medium text-violet-700 hover:text-violet-900 px-3 py-2 rounded-full border border-violet-300"
                    data-testid="open-gallery-btn"
                  >
                    Choose from gallery
                  </button>
                </div>
              </div>
            )}
          </div>

          {cameraOpen && (
            <LiveCameraCapture
              onCapture={handleLiveCapture}
              onCancel={() => setCameraOpen(false)}
            />
          )}

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Deal Title</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Buy 1 Get 1 Free Strawberries" className="text-sm" data-testid="post-deal-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe the deal..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 min-h-[60px]"
              data-testid="post-deal-description"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Store Name</label>
            <StoreAutocomplete
              value={form.store_name}
              onChange={(v) => setForm({ ...form, store_name: v })}
              onAddressChange={(addr) => setForm(prev => ({ ...prev, store_address: addr }))}
              userId={getUserId()}
            />
            {form.store_address && (
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1 truncate" data-testid="store-address-preview">
                <MapPin className="h-3 w-3 text-green-500 flex-shrink-0" />
                {form.store_address}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Sale Price ($)</label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="2.99" className="text-sm" data-testid="post-deal-price" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Original Price ($)</label>
              <Input type="number" step="0.01" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} placeholder="4.99" className="text-sm" data-testid="post-deal-original-price" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((cat) => (
                <button
                  key={cat} type="button"
                  onClick={() => setForm({ ...form, category: form.category === cat ? "" : cat })}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${form.category === cat ? "bg-violet-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <Button
            type="submit"
            disabled={posting || !form.description.trim() || uploading}
            className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
            data-testid="submit-deal"
          >
            {posting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Posting...</> : <><Sparkles className="h-4 w-4 mr-2" />Share Deal</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

// ── Saved Deals ─────────────────────────────────────────────────────────

const SavedDealsView = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [totalSavings, setTotalSavings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    (async () => {
      if (!userId) { setLoading(false); return; }
      try {
        const res = await fetch(`${BACKEND_URL}/api/community/cart?user_id=${userId}`);
        if (res.ok) { const d = await res.json(); setItems(d.items || []); setTotalSavings(d.total_savings || 0); }
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [userId]);

  const handleCardClick = async (item) => {
    if (expandedDeal?.deal_id === item.deal_id) {
      setExpandedDeal(null);
      return;
    }
    setLoadingDetail(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/deals/${item.deal_id}/public`);
      if (res.ok) {
        const data = await res.json();
        setExpandedDeal({ ...item, ...data.deal, deal_id: item.deal_id });
      } else {
        setExpandedDeal(item);
      }
    } catch (e) {
      setExpandedDeal(item);
    }
    setLoadingDetail(false);
  };

  const handleUnsave = async (dealId) => {
    try {
      await fetch(`${BACKEND_URL}/api/community/save`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, deal_id: dealId, deal_type: "community" }),
      });
      setItems((prev) => prev.filter((i) => i.deal_id !== dealId));
      if (expandedDeal?.deal_id === dealId) setExpandedDeal(null);
    } catch (e) { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 text-violet-400 animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="saved-deals-view">
      {totalSavings > 0 && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-green-600 mb-1">Your Potential Savings</p>
            <p className="text-3xl font-bold text-green-700">${totalSavings.toFixed(2)}</p>
            <p className="text-xs text-green-500 mt-1">{items.length} saved deal{items.length !== 1 ? "s" : ""}</p>
          </CardContent>
        </Card>
      )}
      {items.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="p-8 text-center">
            <Bookmark className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No saved deals yet</p>
            <p className="text-sm text-gray-400 mt-1">Bookmark deals from the feed to save them here</p>
          </CardContent>
        </Card>
      ) : items.map((item) => {
        const isExpanded = expandedDeal?.deal_id === item.deal_id;
        return (
          <Card
            key={item.deal_id}
            className={`bg-white cursor-pointer transition-all hover:shadow-md ${isExpanded ? "ring-2 ring-violet-300 shadow-md" : ""}`}
            onClick={() => handleCardClick(item)}
            data-testid={`saved-${item.deal_id}`}
          >
            <CardContent className="p-0">
              {/* Compact row */}
              <div className="p-3 flex items-center gap-3">
                {item.image_url ? (
                  <img src={item.image_url} alt="" className="w-12 h-12 rounded-lg object-contain bg-gray-50 flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><Bookmark className="h-5 w-5 text-amber-400" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900 truncate">{item.title}</p>
                  {item.store && <p className="text-xs text-gray-500">{item.store}</p>}
                </div>
                <div className="text-right flex-shrink-0 flex items-center gap-2">
                  {item.price != null && <p className="font-bold text-green-600">${item.price.toFixed(2)}</p>}
                  <ChevronRight className={`h-4 w-4 text-gray-300 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="border-t border-gray-100 p-3 space-y-3 animate-in slide-in-from-top-2" data-testid={`saved-detail-${item.deal_id}`}>
                  {loadingDetail ? (
                    <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 text-violet-400 animate-spin" /></div>
                  ) : (
                    <>
                      {/* Large image */}
                      {expandedDeal.image_url && (
                        <img src={expandedDeal.image_url} alt="" className="w-full h-40 object-contain rounded-lg bg-gray-50" />
                      )}
                      {/* Description */}
                      {expandedDeal.description && (
                        <p className="text-sm text-gray-700">{expandedDeal.description}</p>
                      )}
                      {/* Price details */}
                      <div className="flex items-center gap-3">
                        {expandedDeal.price != null && (
                          <span className="text-lg font-bold text-green-600">${expandedDeal.price.toFixed(2)}</span>
                        )}
                        {(expandedDeal.original_price != null && expandedDeal.original_price > 0) && (
                          <span className="text-sm text-gray-400 line-through">${expandedDeal.original_price.toFixed(2)}</span>
                        )}
                        {expandedDeal.savings > 0 && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Save ${expandedDeal.savings.toFixed(2)}</span>
                        )}
                      </div>
                      {/* Store + category */}
                      <div className="flex flex-wrap gap-2">
                        {(expandedDeal.store_name || expandedDeal.store) && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{expandedDeal.store_name || expandedDeal.store}</span>
                        )}
                        {expandedDeal.category && (
                          <span className="text-xs bg-violet-100 text-violet-600 px-2 py-1 rounded-full">{expandedDeal.category}</span>
                        )}
                      </div>
                      {expandedDeal.store_address && (
                        <p className="text-xs text-gray-400 flex items-center gap-0.5 truncate">
                          <MapPin className="h-2.5 w-2.5 flex-shrink-0" />{expandedDeal.store_address}
                        </p>
                      )}
                      {(expandedDeal.store_address || (expandedDeal.latitude && expandedDeal.longitude)) && (
                        <DirectionsLink deal={expandedDeal} />
                      )}
                      {/* Posted by + time */}
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>Posted by {expandedDeal.posted_by || expandedDeal.username || "Community"}</span>
                        {expandedDeal.created_at && <span>{timeAgo(expandedDeal.created_at)}</span>}
                      </div>
                      {/* Actions */}
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleUnsave(item.deal_id); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          data-testid={`unsave-${item.deal_id}`}
                        >
                          <Bookmark className="h-3.5 w-3.5 fill-red-500" /> Remove
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ── Notification Panel ───────────────────────────────────────────────────

const NotificationPanel = ({ userId, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/community/notifications?user_id=${userId}`);
        if (res.ok) {
          const data = await res.json();
          setNotifications(data.notifications || []);
        }
      } catch (e) { /* ignore */ }
      setLoading(false);

      // Mark as read
      try {
        await fetch(`${BACKEND_URL}/api/community/notifications/mark-read`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
      } catch (e) { /* ignore */ }
    })();
  }, [userId]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="notification-panel-overlay">
      <div
        className="bg-white rounded-2xl w-full max-w-md max-h-[70vh] overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-violet-500" />
            <h3 className="font-bold text-gray-900 text-sm">Local Deal Alerts</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full" data-testid="close-notifications">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 text-violet-400 animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 font-medium">No new deals nearby</p>
              <p className="text-xs text-gray-400 mt-1">We'll notify you when someone posts a deal in your area</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {notifications.map((n) => (
                <div key={n.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors" data-testid={`notif-${n.id}`}>
                  {n.image_url ? (
                    <img src={n.image_url} alt="" className="w-10 h-10 object-contain rounded-lg bg-gray-50 flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <Tag className="h-4 w-4 text-violet-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{n.title}</p>
                    <p className="text-xs text-gray-500">
                      {n.store_name && <><Store className="h-3 w-3 inline mr-0.5" />{n.store_name} · </>}
                      by <span className="font-medium">{n.posted_by}</span>
                    </p>
                    <p className="text-xs text-gray-400">{timeAgo(n.created_at)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {n.price != null && <p className="text-sm font-bold text-green-600">${n.price.toFixed(2)}</p>}
                    {n.savings > 0 && <p className="text-xs text-green-500">-${n.savings.toFixed(2)}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Gamification Header ─────────────────────────────────────────────────

const GamificationHeader = ({ userId, stats, onRefresh }) => {
  if (!stats) return null;

  const { points, level, streak_count, weekly_points, total_impact, leaderboard_rank, total_verified_deals, deals_posted } = stats;

  return (
    <Card className="overflow-hidden border-0 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-purple-700 text-white" data-testid="gamification-header">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Crown className="h-5 w-5 text-amber-300" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-bold">Level {level}</span>
                <Badge className="bg-white/20 text-white text-xs border-0">{points} pts</Badge>
              </div>
              <div className="flex items-center gap-2 text-xs text-white/70">
                {streak_count > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Flame className={`h-3 w-3 ${streak_count >= 7 ? "text-orange-300" : "text-white/60"}`} />
                    {streak_count}d streak
                  </span>
                )}
                <span>#{leaderboard_rank} this week</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-white/60">Weekly</p>
            <p className="text-sm font-bold">{weekly_points} pts</p>
          </div>
        </div>

        {/* Progress to next level */}
        <div className="mb-2">
          <div className="flex justify-between text-xs text-white/60 mb-0.5">
            <span>Level {level}</span>
            <span>Level {level + 1}</span>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-300 to-amber-400 rounded-full transition-all duration-500"
              style={{ width: `${(points % 100)}%` }}
              data-testid="level-progress-bar"
            />
          </div>
        </div>

        {/* Quick stats row */}
        <div className="grid grid-cols-4 gap-1.5">
          <div className="bg-white/10 rounded-lg p-1.5 text-center backdrop-blur-sm">
            <p className="text-sm font-bold" data-testid="stat-deals-posted">{deals_posted}</p>
            <p className="text-xs text-white/60">Deals</p>
          </div>
          <div className="bg-white/10 rounded-lg p-1.5 text-center backdrop-blur-sm">
            <p className="text-sm font-bold" data-testid="stat-verified-deals">{total_verified_deals}</p>
            <p className="text-xs text-white/60">Verified</p>
          </div>
          <div className="bg-white/10 rounded-lg p-1.5 text-center backdrop-blur-sm">
            <p className="text-sm font-bold" data-testid="stat-streak">{streak_count}</p>
            <p className="text-xs text-white/60">Streak</p>
          </div>
          <div className="bg-white/10 rounded-lg p-1.5 text-center backdrop-blur-sm">
            <p className="text-sm font-bold text-green-300" data-testid="stat-impact">${total_impact.toFixed(0)}</p>
            <p className="text-xs text-white/60">Impact</p>
          </div>
        </div>

        {/* Impact message */}
        {total_impact > 0 && (
          <div className="mt-2 bg-white/10 rounded-lg px-2.5 py-1.5 flex items-center gap-2 backdrop-blur-sm" data-testid="impact-banner">
            <Target className="h-4 w-4 text-green-300 flex-shrink-0" />
            <p className="text-xs text-white/90">
              Your deals helped others save <span className="font-bold text-green-300">${total_impact.toFixed(2)}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Leaderboard View ────────────────────────────────────────────────────

const LeaderboardView = ({ userId }) => {
  const [leaders, setLeaders] = useState([]);
  const [period, setPeriod] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/community/leaderboard?period=${period}`);
        if (res.ok) setLeaders((await res.json()).leaderboard || []);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [period]);

  const loadHistory = async () => {
    if (history.length > 0) { setShowHistory(!showHistory); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/leaderboard/history`);
      if (res.ok) { setHistory((await res.json()).history || []); setShowHistory(true); }
    } catch (e) { /* ignore */ }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-amber-400" />;
    if (rank === 2) return <Trophy className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Trophy className="h-5 w-5 text-amber-700" />;
    return <span className="text-xs font-bold text-gray-500 w-5 text-center">#{rank}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return "bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200";
    if (rank === 2) return "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200";
    if (rank === 3) return "bg-gradient-to-r from-amber-50/50 to-orange-50/50 border-amber-100";
    return "bg-white border-gray-100";
  };

  return (
    <div className="space-y-3" data-testid="leaderboard-view">
      {/* Period Toggle */}
      <div className="flex gap-1.5">
        {[
          { key: "weekly", label: "This Week", icon: Zap },
          { key: "alltime", label: "All Time", icon: Trophy },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setPeriod(key)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors ${
              period === key ? "bg-violet-500 text-white font-semibold" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}
            data-testid={`lb-period-${key}`}
          >
            <Icon className="h-3 w-3" />{label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={loadHistory}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs bg-gray-100 text-gray-500 hover:bg-gray-200"
          data-testid="lb-history-toggle"
        >
          <Clock className="h-3 w-3" />Past Weeks
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 text-violet-400 animate-spin" /></div>
      ) : leaders.length === 0 ? (
        <Card className="border-dashed border-2 border-violet-200 bg-violet-50/30">
          <CardContent className="p-8 text-center">
            <Trophy className="h-10 w-10 text-violet-300 mx-auto mb-3" />
            <p className="text-violet-800 font-medium">No leaders yet</p>
            <p className="text-sm text-violet-500 mt-1">Post and verify deals to climb the ranks!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5">
          {leaders.map((u) => (
            <div
              key={u.id}
              className={`flex items-center gap-2.5 p-2.5 rounded-xl border transition-all ${getRankStyle(u.rank)} ${u.id === userId ? "ring-2 ring-violet-400 ring-offset-1" : ""}`}
              data-testid={`lb-user-${u.rank}`}
            >
              <div className="flex-shrink-0 w-6 flex justify-center">
                {getRankIcon(u.rank)}
              </div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {(u.username || "A")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-gray-800 truncate">{u.username || "Anonymous"}</p>
                  <Badge className="bg-violet-100 text-violet-700 text-xs border-0">Lv.{u.level || 1}</Badge>
                  {u.streak_count >= 3 && (
                    <span className="flex items-center text-xs text-orange-500">
                      <Flame className="h-3 w-3" />{u.streak_count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {u.deals_posted || 0} deals · {u.total_verified_deals || 0} verified
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-violet-600">
                  {period === "weekly" ? u.weekly_points : u.points}
                </p>
                <p className="text-xs text-gray-400">pts</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Past Weeks History */}
      {showHistory && history.length > 0 && (
        <div className="space-y-2 mt-3" data-testid="lb-history">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Past Winners</p>
          {history.map((week) => (
            <Card key={week.id} className="bg-gray-50 border-gray-200">
              <CardContent className="p-2.5">
                <p className="text-xs font-semibold text-gray-700 mb-1.5">Week {week.week}</p>
                <div className="space-y-1">
                  {(week.top_users || []).slice(0, 3).map((u) => (
                    <div key={u.user_id} className="flex items-center gap-2 text-xs">
                      {getRankIcon(u.rank)}
                      <span className="font-medium text-gray-700 flex-1">{u.username}</span>
                      <span className="text-violet-600 font-semibold">{u.weekly_points} pts</span>
                      <span className="text-green-600">+{u.bonus}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Incentive Tracker ────────────────────────────────────────────────────

const IncentiveTracker = ({ userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/community/incentives/progress?user_id=${userId}`);
        if (res.ok) setData(await res.json());
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [userId]);

  if (loading || !data) return null;

  const { verified_count, total_posted, pending_verification, current_tier, next_milestone, tiers, base_price, active_reward } = data;
  const silverT = tiers.silver.threshold;
  const goldT = tiers.gold.threshold;
  const progressPct = next_milestone ? Math.min((verified_count / next_milestone) * 100, 100) : 100;
  const nextLabel = verified_count < silverT ? `${silverT - verified_count} more for 50% off` : verified_count < goldT ? `${goldT - verified_count} more for $1.99/mo` : "Max tier reached!";

  return (
    <Card className="overflow-hidden border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50" data-testid="incentive-tracker">
      <CardContent className="p-0">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-violet-50/50 transition-colors"
          data-testid="incentive-toggle"
        >
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
              current_tier === "gold" ? "bg-amber-100" : current_tier === "silver" ? "bg-gray-200" : "bg-violet-100"
            }`}>
              <Award className={`h-4 w-4 ${
                current_tier === "gold" ? "text-amber-600" : current_tier === "silver" ? "text-gray-600" : "text-violet-500"
              }`} />
            </div>
            <div className="text-left">
              <p className="text-xs font-bold text-gray-800">Deal Rewards</p>
              <p className="text-xs text-gray-500">
                {verified_count} verified
                {next_milestone && <span className="text-violet-600 font-medium"> · {nextLabel}</span>}
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>

        {/* Progress Bar (always visible) */}
        <div className="px-3.5 pb-2">
          <div className="relative h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${
                current_tier === "gold"
                  ? "bg-gradient-to-r from-amber-400 to-amber-500"
                  : current_tier === "silver"
                  ? "bg-gradient-to-r from-gray-400 to-gray-500"
                  : "bg-gradient-to-r from-violet-400 to-fuchsia-500"
              }`}
              style={{ width: `${progressPct}%` }}
              data-testid="incentive-progress-bar"
            />
            {/* Milestone markers */}
            {verified_count < goldT && (
              <>
                <div className="absolute top-0 bottom-0 w-0.5 bg-gray-300" style={{ left: `${(silverT / (next_milestone || goldT)) * 100}%` }} />
                {next_milestone === goldT && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-gray-300" style={{ left: "100%" }} />
                )}
              </>
            )}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-400">{verified_count}/{next_milestone || goldT}</span>
            <span className={`text-xs font-semibold ${
              current_tier === "gold" ? "text-amber-600" : current_tier === "silver" ? "text-gray-600" : "text-violet-500"
            }`}>
              {current_tier === "gold" ? "Gold Tier" : current_tier === "silver" ? "Silver Tier" : "Building"}
            </span>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-violet-100 px-3.5 py-3 space-y-3">
            {/* Active Reward Banner */}
            {active_reward && (
              <div className={`rounded-lg p-2.5 ${
                active_reward.tier === "gold" ? "bg-amber-50 border border-amber-200" : "bg-gray-50 border border-gray-200"
              }`} data-testid="active-reward-banner">
                <div className="flex items-center gap-2">
                  <Star className={`h-4 w-4 ${active_reward.tier === "gold" ? "text-amber-500 fill-amber-500" : "text-gray-500 fill-gray-500"}`} />
                  <div>
                    <p className="text-xs font-bold text-gray-800">
                      Active: ${active_reward.price}/mo
                      <span className="text-green-600 ml-1">(saving ${active_reward.savings})</span>
                    </p>
                    <p className="text-xs text-gray-500">{active_reward.reason}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white rounded-lg p-2 text-center border border-gray-100">
                <p className="text-lg font-bold text-violet-600" data-testid="stat-verified">{verified_count}</p>
                <p className="text-xs text-gray-500">Verified</p>
              </div>
              <div className="bg-white rounded-lg p-2 text-center border border-gray-100">
                <p className="text-lg font-bold text-gray-700" data-testid="stat-posted">{total_posted}</p>
                <p className="text-xs text-gray-500">Posted</p>
              </div>
              <div className="bg-white rounded-lg p-2 text-center border border-gray-100">
                <p className="text-lg font-bold text-orange-500" data-testid="stat-pending">{pending_verification}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>

            {/* Tier Breakdown */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Reward Tiers</p>
              <div className={`flex items-center gap-2.5 p-2 rounded-lg transition-colors ${
                current_tier === "silver" || current_tier === "gold" ? "bg-gray-100" : "bg-white border border-gray-100"
              }`} data-testid="tier-silver">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  verified_count >= silverT ? "bg-gray-500" : "bg-gray-200"
                }`}>
                  <Award className={`h-3.5 w-3.5 ${verified_count >= silverT ? "text-white" : "text-gray-400"}`} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-800">Silver — {silverT} deals</p>
                  <p className="text-xs text-gray-500">${tiers.silver.price}/mo ({tiers.silver.label})</p>
                </div>
                {verified_count >= silverT && <ShieldCheck className="h-4 w-4 text-green-500" />}
              </div>
              <div className={`flex items-center gap-2.5 p-2 rounded-lg transition-colors ${
                current_tier === "gold" ? "bg-amber-50 border border-amber-200" : "bg-white border border-gray-100"
              }`} data-testid="tier-gold">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  verified_count >= goldT ? "bg-amber-500" : "bg-gray-200"
                }`}>
                  <Award className={`h-3.5 w-3.5 ${verified_count >= goldT ? "text-white" : "text-gray-400"}`} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-800">Gold — {goldT} deals</p>
                  <p className="text-xs text-gray-500">${tiers.gold.price}/mo ({tiers.gold.label})</p>
                </div>
                {verified_count >= goldT && <ShieldCheck className="h-4 w-4 text-green-500" />}
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center">
              Post deals that 5+ users verify to earn rewards. Resets monthly.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Main Component ──────────────────────────────────────────────────────

const CommunityDealsTab = ({ highlightDealId, highlightItem = "", onDealViewed, onHighlightHandled, guestZip = "", zipPromptOpen = false }) => {
  const [userId, setUserId] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dotd, setDotd] = useState(null);
  const [likedSet, setLikedSet] = useState(new Set());
  const [savedSet, setSavedSet] = useState(new Set());
  const [verifiedSet, setVerifiedSet] = useState(new Set());
  const [followingSet, setFollowingSet] = useState(new Set());
  const [expandedComments, setExpandedComments] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);
  const [sortMode, setSortMode] = useState("newest");
  const [subView, setSubView] = useState("feed");
  const [location, setLocation] = useState(null);
  const [feedFilter, setFeedFilter] = useState("all_deals");
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dealNotifications, setDealNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [gamificationStats, setGamificationStats] = useState(null);
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [feedDays, setFeedDays] = useState(null);
  const [showLocationConsent, setShowLocationConsent] = useState(false);
  const [userGps, setUserGps] = useState(null);
  const highlightRef = useRef(null);

  // Show location consent prompt on first visit (if user hasn't decided yet).
  // Deferred while the Deals ZIP/radius modal is open so prompts never stack.
  useEffect(() => {
    if (zipPromptOpen) return;
    const consent = getLocationConsent();
    if (!consent && navigator.geolocation) {
      const timer = setTimeout(() => setShowLocationConsent(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [zipPromptOpen]);

  // Detect GPS for radius filtering
  useEffect(() => {
    const consent = getLocationConsent();
    if (consent === "granted" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { timeout: 8000, enableHighAccuracy: true }
      );
    }
  }, []);

  // Scroll to highlighted deal when feed loads
  useEffect(() => {
    if (highlightDealId && deals.length > 0 && !loading) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="deal-card-${highlightDealId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-violet-400", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-violet-400", "ring-offset-2"), 3000);
          onDealViewed?.();
        }
      }, 300);
    }
  }, [highlightDealId, deals, loading, onDealViewed]);

  // Load deal activity notifications
  const loadNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/notifications/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setDealNotifications(data.notifications || []);
        setUnreadCount(data.unread || 0);
      }
    } catch { /* ignore */ }
  }, [userId]);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  // Poll for new notifications every 30s
  useEffect(() => {
    if (!userId) return;
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [userId, loadNotifications]);

  const markNotificationsRead = async () => {
    if (!userId || unreadCount === 0) return;
    await fetch(`${BACKEND_URL}/api/community/notifications/read`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    }).catch(() => {});
    setUnreadCount(0);
    setDealNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

  const loadFeed = useCallback(async (sort = sortMode, loc = location, filter = feedFilter, radius = radiusMiles, days = feedDays) => {
    try {
      let url = `${BACKEND_URL}/api/community/feed?sort=${sort}&radius_miles=${radius}`;
      // Guest ZIP (from localStorage) wins over the empty default `location`
      const effectiveLoc = loc || (loc === "all" ? null : guestZip);
      if (effectiveLoc === "all") url += "&location=all";
      else if (effectiveLoc) url += `&location=${effectiveLoc}`;
      if (filter === "following" && userId) url += `&following_only=true&user_id=${userId}`;
      if (days) url += `&days=${days}`;
      // Pass GPS coordinates for accurate radius filtering
      if (userGps) url += `&lat=${userGps.lat}&lng=${userGps.lng}`;
      const res = await fetch(url);
      if (res.ok) setDeals((await res.json()).deals || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [sortMode, location, feedFilter, userId, radiusMiles, feedDays, userGps, guestZip]);

  const loadGamificationStats = useCallback(async (uid) => {
    const id = uid || userId;
    if (!id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/user/${id}/stats`);
      if (res.ok) setGamificationStats(await res.json());
    } catch (e) { /* ignore */ }
  }, [userId]);

  useEffect(() => {
    const init = async () => {
      const user = await ensureUser();
      if (user) {
        setUserId(user.id);
        setAlertsEnabled(!!user.local_deal_alerts);
        if (user.radius_miles) setRadiusMiles(user.radius_miles);
        // Fetch referral code from auth
        try {
          const token = localStorage.getItem("token");
          if (token) {
            const refRes = await fetch(`${BACKEND_URL}/api/auth/referral/stats`, { headers: { Authorization: `Bearer ${token}` } });
            if (refRes.ok) {
              const refData = await refRes.json();
              setReferralCode(refData.referral_code);
            }
          }
        } catch { /* ignore */ }
        try {
          const [iRes, fRes] = await Promise.all([
            fetch(`${BACKEND_URL}/api/community/interactions?user_id=${user.id}`),
            fetch(`${BACKEND_URL}/api/community/following?user_id=${user.id}`),
          ]);
          if (iRes.ok) {
            const iData = await iRes.json();
            setLikedSet(new Set(iData.likes || []));
            setSavedSet(new Set(iData.saves || []));
            setVerifiedSet(new Set(iData.verifies || []));
          }
          if (fRes.ok) {
            setFollowingSet(new Set((await fRes.json()).following || []));
          }
        } catch (e) { /* ignore */ }

        // Fetch gamification stats
        loadGamificationStats(user.id);

        // Fetch notification count if alerts are enabled
        if (user.local_deal_alerts) {
          try {
            const nRes = await fetch(`${BACKEND_URL}/api/community/notifications?user_id=${user.id}`);
            if (nRes.ok) {
              const nData = await nRes.json();
              setNotifCount(nData.count || 0);
            }
          } catch (e) { /* ignore */ }
          // Auto-register push if supported and alerts are enabled
          if (isPushSupported() && Notification.permission === "granted") {
            registerPushSubscription(user.id).catch(() => {});
          }
        }
      }
      // Load deal of the day
      try {
        const dRes = await fetch(`${BACKEND_URL}/api/community/deal-of-the-day`);
        if (dRes.ok) {
          const dData = await dRes.json();
          if (dData.found) setDotd(dData.deal);
        }
      } catch (e) { /* ignore */ }
      await loadFeed();
    };
    init();
  }, []); // eslint-disable-line

  const showRewardToasts = (rewards) => {
    if (!rewards || rewards.length === 0) return;
    rewards.forEach((r, i) => {
      setTimeout(() => {
        toast.success(`+${r.points} pts: ${r.description}`, { duration: 3000 });
      }, i * 500);
    });
    // Refresh gamification stats after rewards
    setTimeout(() => loadGamificationStats(), 1000);
  };

  const handleLike = async (dealId, dealType) => {
    const res = await fetch(`${BACKEND_URL}/api/community/like`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, deal_id: dealId, deal_type: dealType }),
    });
    if (res.ok) {
      const data = await res.json();
      setLikedSet((prev) => { const n = new Set(prev); data.liked ? n.add(dealId) : n.delete(dealId); return n; });
      setDeals((prev) => prev.map((d) => d.id === dealId ? { ...d, likes_count: (d.likes_count || 0) + (data.liked ? 1 : -1) } : d));
      if (data.reward) showRewardToasts([data.reward]);
    }
  };

  const handleRadiusChange = async (newRadius) => {
    setRadiusMiles(newRadius);
    setLoading(true);
    if (userId) {
      fetch(`${BACKEND_URL}/api/community/user/${userId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ radius_miles: newRadius }),
      }).catch(() => {});
    }
    loadFeed(sortMode, location, feedFilter, newRadius);
  };


  const handleSave = async (dealId, dealType, deal) => {
    const snapshot = deal ? {
      title: deal.title || deal.description?.slice(0, 80) || "",
      store_name: deal.store_name || deal.store || "",
      price: deal.price,
      original_price: deal.original_price,
      savings: deal.savings || 0,
      image_url: deal.image_url || "",
    } : undefined;
    const res = await fetch(`${BACKEND_URL}/api/community/save`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, deal_id: dealId, deal_type: dealType, deal_snapshot: snapshot }),
    });
    if (res.ok) {
      const data = await res.json();
      setSavedSet((prev) => { const n = new Set(prev); data.saved ? n.add(dealId) : n.delete(dealId); return n; });
      setDeals((prev) => prev.map((d) => d.id === dealId ? { ...d, saves_count: (d.saves_count || 0) + (data.saved ? 1 : -1) } : d));
    }
  };

  const handleVerify = async (dealId) => {
    const res = await fetch(`${BACKEND_URL}/api/community/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, deal_id: dealId }),
    });
    if (res.ok) {
      const data = await res.json();
      setVerifiedSet((prev) => new Set(prev).add(dealId));
      setDeals((prev) => prev.map((d) => d.id === dealId ? { ...d, verify_count: data.total_verifications || (d.verify_count || 0) + 1 } : d));
      if (data.reward) showRewardToasts([data.reward]);
    }
  };

  const handleFollow = async (targetId) => {
    if (!targetId || !userId) return;
    const res = await fetch(`${BACKEND_URL}/api/community/follow`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ follower_id: userId, target_id: targetId }),
    });
    if (res.ok) {
      const data = await res.json();
      setFollowingSet((prev) => { const n = new Set(prev); data.following ? n.add(targetId) : n.delete(targetId); return n; });
    }
  };

  const handleCommentPosted = () => {
    setDeals((prev) => prev.map((d) => d.id === expandedComments ? { ...d, comments_count: (d.comments_count || 0) + 1 } : d));
  };

  const handleDealPosted = (rewards) => { loadFeed("newest"); setSortMode("newest"); showRewardToasts(rewards); };

  const handleDeleteDeal = async (dealId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/deals/${dealId}?user_id=${userId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setDeals((prev) => prev.filter((d) => d.id !== dealId));
      }
    } catch (e) { console.error("Delete failed:", e); }
  };

  const [reportedSet, setReportedSet] = useState(new Set());

  const handleReport = async (dealId, reason) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/deals/${dealId}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, reason }),
      });
      if (res.ok) {
        setReportedSet(prev => new Set([...prev, dealId]));
      }
    } catch (e) { console.error("Report failed:", e); }
  };

  const handleToggleAlerts = async () => {
    if (!userId) return;
    const newVal = !alertsEnabled;
    setAlertsEnabled(newVal);
    try {
      await fetch(`${BACKEND_URL}/api/community/user/${userId}/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ local_deal_alerts: newVal }),
      });
      if (newVal) {
        // Register push notifications
        if (isPushSupported()) {
          await registerPushSubscription(userId);
        }
        // Fetch notification count
        const nRes = await fetch(`${BACKEND_URL}/api/community/notifications?user_id=${userId}`);
        if (nRes.ok) {
          const nData = await nRes.json();
          setNotifCount(nData.count || 0);
        }
      } else {
        setNotifCount(0);
        // Unregister push notifications
        if (isPushSupported()) {
          await unregisterPushSubscription(userId);
        }
      }
    } catch (e) { /* ignore */ }
  };

  const handleOpenNotifications = () => {
    setShowNotifications(true);
    setNotifCount(0);
  };

  const handleLocationChange = (loc) => { setLocation(loc); loadFeed(sortMode, loc, feedFilter); };

  const handleFeedFilterChange = (filter) => { setFeedFilter(filter); loadFeed(sortMode, location, filter); };

  const handleDaysChange = (d) => {
    const newDays = feedDays === d ? null : d;
    setFeedDays(newDays);
    loadFeed(sortMode, location, feedFilter, radiusMiles, newDays);
  };

  return (
    <div className="space-y-3" data-testid="community-deals-tab">
      {/* Location Consent Prompt */}
      {showLocationConsent && (
        <LocationConsentPrompt
          onConsent={() => setShowLocationConsent(false)}
          onDismiss={() => setShowLocationConsent(false)}
        />
      )}
      {/* Sub-navigation */}
      <div className="flex gap-1 items-center -mx-1">
        <Button
          size="sm"
          variant={subView === "feed" ? "default" : "outline"}
          onClick={() => setSubView("feed")}
          className={`flex-1 text-xs px-1.5 min-w-0 ${subView === "feed" ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-0" : "border-gray-300 text-gray-600"}`}
          data-testid="sub-feed"
        >
          <Users className="h-3 w-3 mr-0.5 flex-shrink-0" /> Feed
        </Button>
        <Button
          size="sm"
          variant={subView === "saved" ? "default" : "outline"}
          onClick={() => setSubView("saved")}
          className={`flex-1 text-xs px-1.5 min-w-0 ${subView === "saved" ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-0" : "border-gray-300 text-gray-600"}`}
          data-testid="sub-saved"
        >
          <Bookmark className="h-3 w-3 mr-0.5 flex-shrink-0" /> Saved
        </Button>
        <Button
          size="sm"
          variant={subView === "leaderboard" ? "default" : "outline"}
          onClick={() => setSubView("leaderboard")}
          className={`flex-1 text-xs px-1.5 min-w-0 ${subView === "leaderboard" ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-0" : "border-gray-300 text-gray-600"}`}
          data-testid="sub-leaderboard"
        >
          <Trophy className="h-3 w-3 mr-0.5 flex-shrink-0" /> Rank
        </Button>
        <Button
          size="sm"
          onClick={() => setShowPostForm(true)}
          className="bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white text-xs border-0 flex-1 px-1.5 min-w-0"
          data-testid="post-deal-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-0.5 flex-shrink-0" /> Post
        </Button>
      </div>

      {/* Make Grocery List shortcut — prominent CTA into the functional list builder */}
      <a
        href="/home"
        className="mx-1 flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-3 shadow-sm hover:shadow-md hover:border-emerald-400 transition-all"
        data-testid="make-grocery-list-cta"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">Make a Grocery List</p>
            <p className="text-[11px] text-gray-500 truncate">Type or speak your items — deal alerts follow.</p>
          </div>
        </div>
        <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
          Start
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </a>

      {/* Activity Notifications Panel */}
      {showNotifications && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-lg mx-1 max-h-64 overflow-y-auto" data-testid="notif-panel">
          <div className="p-2.5 border-b border-gray-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">Activity on Your Deals</span>
            <button onClick={() => setShowNotifications(false)} className="text-xs text-gray-400 hover:text-gray-600">Close</button>
          </div>
          {dealNotifications.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">No activity yet. Post a deal to get started!</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {dealNotifications.slice(0, 20).map((n) => (
                <div key={n.id} className={`px-3 py-2 text-xs ${n.read ? "text-gray-500" : "text-gray-800 bg-violet-50/30"}`} data-testid={`notif-${n.id}`}>
                  <p>{n.title}</p>
                  <p className="text-gray-300 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Radius filter — inline on feed */}
      {subView === "feed" && (
        <div className="flex items-center gap-2 px-1" data-testid="radius-filter">
          <Navigation className="h-3.5 w-3.5 text-violet-500 flex-shrink-0" />
          <span className="text-xs text-gray-500 flex-shrink-0">Within</span>
          <div className="flex gap-1 flex-wrap">
            {RADIUS_OPTIONS.map((r) => (
              <button
                key={r}
                onClick={() => handleRadiusChange(r)}
                className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                  radiusMiles === r
                    ? "bg-violet-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`radius-${r}`}
              >
                {r} mi
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Day filter — how many days of deals to show */}
      {subView === "feed" && (
        <div className="flex items-center gap-2 px-1" data-testid="day-filter">
          <Clock className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />
          <span className="text-xs text-gray-500 flex-shrink-0">Show</span>
          <div className="flex gap-1 flex-wrap flex-1">
            {[1, 2, 3, 5, 7].map((d) => (
              <button
                key={d}
                onClick={() => handleDaysChange(d)}
                className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                  feedDays === d
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`days-${d}`}
              >
                {d}d
              </button>
            ))}
            {feedDays && (
              <button
                onClick={() => handleDaysChange(feedDays)}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-500 hover:bg-gray-300"
                data-testid="days-clear"
              >
                All
              </button>
            )}
          </div>
          {/* Notification Bell */}
          <button
            onClick={() => { setShowNotifications(!showNotifications); if (!showNotifications) markNotificationsRead(); }}
            className="relative p-1.5 rounded-full hover:bg-violet-50 transition-colors flex-shrink-0"
            data-testid="notification-bell-btn"
          >
            <Bell className={`h-4 w-4 ${unreadCount > 0 ? "text-violet-600" : "text-gray-400"}`} />
            {unreadCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5"
                data-testid="notif-unread-count"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        </div>
      )}

      {subView === "saved" && <SavedDealsView userId={userId} />}
      {subView === "leaderboard" && <LeaderboardView userId={userId} />}

      {subView === "feed" && (
        <>
          {/* Gamification Header */}
          {userId && <GamificationHeader userId={userId} stats={gamificationStats} />}

          {/* Location filter */}
          <LocationFilter currentLocation={location} onLocationChange={handleLocationChange} />

          {/* Incentive Progress Tracker */}
          {userId && <IncentiveTracker userId={userId} />}

          {/* Feed filter: All Deals / Following */}
          <div className="flex gap-1.5">
            {[
              { key: "all_deals", label: "All Deals", icon: TrendingUp },
              { key: "following", label: "Following", icon: UserCheck },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => handleFeedFilterChange(key)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs transition-colors ${
                  feedFilter === key ? "bg-violet-100 text-violet-700 font-semibold" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
                data-testid={`filter-${key}`}
              >
                <Icon className="h-3 w-3" />{label}
              </button>
            ))}
            <div className="flex-1" />
            {[
              { key: "trending", label: "Trending", icon: TrendingUp },
              { key: "newest", label: "New", icon: Clock },
              { key: "savings", label: "Savings", icon: DollarSign },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => { setSortMode(key); loadFeed(key); }}
                className={`flex items-center gap-0.5 px-2 py-1.5 rounded-full text-xs transition-colors ${
                  sortMode === key ? "bg-gray-200 text-gray-800 font-semibold" : "bg-gray-100 text-gray-400"
                }`}
                data-testid={`sort-${key}`}
              >
                <Icon className="h-3 w-3" />{label}
              </button>
            ))}
          </div>

          {/* Deal of the Day */}
          {dotd && (
            <DealOfTheDay
              deal={dotd}
              liked={likedSet.has(dotd.id)}
              saved={savedSet.has(dotd.id)}
              onLike={handleLike}
              onSave={handleSave}
            />
          )}

          {/* Feed */}
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-violet-400 animate-spin" /></div>
          ) : deals.length === 0 ? (
            <Card className="border-dashed border-2 border-violet-200 bg-violet-50/30">
              <CardContent className="p-8 text-center">
                <Users className="h-10 w-10 text-violet-300 mx-auto mb-3" />
                <p className="text-violet-800 font-medium">
                  {feedFilter === "following" ? "No deals from people you follow" : "No deals yet in your community"}
                </p>
                <p className="text-sm text-violet-500 mt-1 mb-4">
                  {feedFilter === "following" ? "Follow deal sharers to see their posts here" : "Be the first to share a deal!"}
                </p>
                {feedFilter !== "following" && (
                  <Button size="sm" onClick={() => setShowPostForm(true)} className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white">
                    <Plus className="h-3 w-3 mr-1" /> Share a Deal
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (() => {
              // Filter feed by highlighted item if user arrived via Home tooltip deep-link
              const q = (highlightItem || "").trim().toLowerCase();
              const feed = q
                ? deals.filter((d) => {
                    const t = (d.title || "").toLowerCase();
                    const desc = (d.description || "").toLowerCase();
                    return t.includes(q) || desc.includes(q);
                  })
                : deals;
              return (
                <div className="space-y-3">
                  {q && (
                    <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 flex items-center gap-2" data-testid="community-deals-deeplink-banner">
                      <span className="text-sm text-violet-800 flex-1">
                        Showing <strong>{feed.length}</strong> community deal{feed.length !== 1 ? "s" : ""} matching <strong>"{highlightItem}"</strong>
                      </span>
                      <button
                        onClick={() => onHighlightHandled?.()}
                        className="text-xs text-violet-700 hover:text-violet-900 underline whitespace-nowrap"
                        data-testid="community-deals-clear-deeplink"
                      >
                        Show all
                      </button>
                    </div>
                  )}
                  {feed.length === 0 ? (
                    <Card className="border-dashed border-2 border-violet-200 bg-violet-50/30">
                      <CardContent className="p-6 text-center">
                        <p className="text-sm text-violet-700">
                          No community deals matching "{highlightItem}" yet. Be the first to share one!
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    feed.map((deal) => (
                      <div key={deal.id}>
                        <DealCard
                          deal={deal}
                          userId={userId}
                          liked={likedSet.has(deal.id)}
                          saved={savedSet.has(deal.id)}
                          verified={verifiedSet.has(deal.id)}
                          isFollowing={followingSet.has(deal.user_id)}
                          reported={reportedSet.has(deal.id)}
                          referralCode={referralCode}
                          onLike={handleLike}
                          onSave={handleSave}
                          onVerify={handleVerify}
                          onFollow={handleFollow}
                          onToggleComments={(id) => setExpandedComments(expandedComments === id ? null : id)}
                          onDelete={handleDeleteDeal}
                          onReport={handleReport}
                        />
                        {expandedComments === deal.id && (
                          <CommentSection dealId={deal.id} onPost={handleCommentPosted} />
                        )}
                      </div>
                    ))
                  )}
                </div>
              );
            })()}
        </>
      )}

      {showPostForm && <PostDealForm onClose={() => setShowPostForm(false)} onPosted={handleDealPosted} userGps={userGps} />}
    </div>
  );
};

export default CommunityDealsTab;
